import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, AlertTriangle, ChevronLeft, ChevronRight, Eye, Check, X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminBookingDetailUrl, ADMIN_MANUAL_BOOKING_URL } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  PENDING: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  PENDING_REVIEW: "bg-red-500/15 text-red-400 border-red-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
  COMPLETED: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  DECLINED: "bg-red-900/15 text-red-300 border-red-900/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  PAID: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

const TABS = ["all", "PENDING", "PENDING_REVIEW", "CONFIRMED", "IN_PROGRESS", "COMPLETED", "CANCELLED", "DECLINED"];
const TAB_LABELS = { all: "All", PENDING: "Pending", PENDING_REVIEW: "Clash Booking", CONFIRMED: "Confirmed", IN_PROGRESS: "In Progress", COMPLETED: "Completed", CANCELLED: "Cancelled", DECLINED: "Declined" };

export default function AdminBookings() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (tab !== "all") params.status = tab;
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/bookings`, { params });
      setBookings(res.data.bookings);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [page, tab, search]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const quickConfirm = async (id) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/confirm`, {});
      toast.success("Booking confirmed");
      fetchBookings();
    } catch { toast.error("Failed to confirm"); }
  };

  const quickDecline = async (id) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/decline`, { reason: "Not available" });
      toast.success("Booking declined");
      fetchBookings();
    } catch { toast.error("Failed to decline"); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="admin-bookings">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-xl font-bold text-white">Bookings</h1>
        <div className="flex items-center gap-3">
          <span className="text-sarathi-text-muted text-sm">{total} total</span>
          <Link to={ADMIN_MANUAL_BOOKING_URL}>
            <Button size="sm" className="h-8 text-xs bg-sarathi-primary hover:bg-sarathi-primary-hover text-white" data-testid="manual-booking-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Manual Booking
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sarathi-text-muted" />
        <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, phone, reference, location..."
          className="pl-10 bg-sarathi-surface border-white/10 text-white rounded-xl h-10" data-testid="search-input" />
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map(t => (
          <button
            type="button"
            key={t} onClick={() => { setTab(t); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${tab === t ? "bg-sarathi-primary text-white" : "bg-sarathi-surface border border-white/10 text-sarathi-text-secondary hover:text-white"}`}
            data-testid={`tab-${t}`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="text-sarathi-text-muted text-center py-10">Loading...</div>
      ) : bookings.length === 0 ? (
        <div className="text-sarathi-text-muted text-center py-10">No bookings found</div>
      ) : (
        <div className="space-y-2">
          {bookings.map(b => {
            const fare = b.fare_data;
            const totalFare = fare?.total || fare?.displayTotal || "—";
            const distance = fare?.distanceKm || b.fare_data?.distanceKm;
            const status = (b.status || "").toUpperCase();
            const needsReview = status === "PENDING_REVIEW" || (status === "PENDING" && fare?.adminFlags?.requiresManualReview);
            const isPending = status === "PENDING" || status === "PENDING_REVIEW";

            return (
              <div key={b.id} className={`bg-sarathi-surface border rounded-xl p-4 ${needsReview ? "border-red-500/40" : "border-white/10"}`} data-testid={`booking-card-${b.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sarathi-primary font-mono text-xs">{b.booking_reference}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status] || STATUS_COLORS.PENDING}`}>{status}</span>
                      {needsReview && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                    </div>
                    <p className="text-white text-sm truncate">{b.pickup_location} → {b.drop_location}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-sarathi-text-muted">
                      <span>{b.date} · {b.time}</span>
                      <span>{b.passenger_name}</span>
                      <span>{b.passenger_phone}</span>
                      {distance && <span>{distance} km</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {typeof totalFare === "number" ? (
                      <span className="text-sarathi-primary font-outfit font-bold text-sm">₹{totalFare.toLocaleString("en-IN")}</span>
                    ) : (
                      <span className="text-sarathi-primary font-outfit font-bold text-sm">{totalFare}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  <Link to={adminBookingDetailUrl(b.id)}>
                    <Button variant="outline" size="sm" className="h-8 text-xs border-white/10 text-sarathi-text-secondary hover:text-white" data-testid={`view-${b.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                  </Link>
                  {isPending && (
                    <>
                      <Button size="sm" onClick={() => quickConfirm(b.id)} className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid={`confirm-${b.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Confirm
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => quickDecline(b.id)} className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`decline-${b.id}`}>
                        <X className="w-3 h-3 mr-1" /> Decline
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-white/10 text-sarathi-text-secondary">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sarathi-text-muted text-sm">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-white/10 text-sarathi-text-secondary">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
